import { Check, ChevronDown, ChevronUp, Contact, Crop, Dumbbell, Eye, Files, FileText, History, Home, Image as ImageIcon, Plus, Save, Scale, ShieldCheck, Trash2, UploadCloud, Users } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ChangeEvent, type Dispatch, type PointerEvent as ReactPointerEvent, type ReactNode, type SetStateAction } from "react";
import { useContent } from "../../context/ContentContext";
import { useAuth } from "../../context/AuthContext";
import { useAdminToast } from "../../context/AdminToastContext";
import { AdminConfirmDialog } from "../../components/AdminConfirmDialog";
import { AdminFormDialog } from "../../components/AdminFormDialog";
import { adminErrorMessage, missingFieldsMessage } from "../../lib/adminFeedback";
import { writeAuditLog } from "../../lib/audit";
import { supabase } from "../../lib/supabase";
import type { BoardMember, DownloadDocument, HeaderImageSettings, HeaderPageKey, SiteContent, SiteSectionKey } from "../../types";
import { AdminPageSkeleton } from "../../components/LoadingSkeleton";

const sections: Array<{ key: SiteSectionKey; label: string; icon: typeof Home }> = [
  { key: "branding", label: "Logo & Marke", icon: ImageIcon },
  { key: "hero", label: "Startseiten-Kopf", icon: Home },
  { key: "headers", label: "Titelbilder", icon: Crop },
  { key: "home", label: "Startseiten-Inhalte", icon: Eye },
  { key: "training", label: "Training", icon: Dumbbell },
  { key: "chronicle", label: "Chronik", icon: History },
  { key: "board", label: "Vorstand", icon: Users },
  { key: "contact", label: "Kontakt", icon: Contact },
  { key: "downloads", label: "Downloads", icon: Files },
  { key: "imprint", label: "Impressum", icon: Scale },
  { key: "privacy", label: "Datenschutz", icon: ShieldCheck },
];

const headerPages: Array<{ key: HeaderPageKey; label: string }> = [
  { key: "home", label: "Startseite" },
  { key: "news", label: "Aktuelles" },
  { key: "teams", label: "Mannschaften" },
  { key: "training", label: "Training" },
  { key: "club", label: "Verein" },
  { key: "contact", label: "Kontakt" },
];

interface PreviewViewport {
  width: number;
  height: number;
}

function previewAspectRatio(isHome: boolean, mode: "desktop" | "mobile", viewport: PreviewViewport): string {
  if (mode === "mobile") {
    const width = viewport.width <= 640 ? viewport.width : 390;
    return `${Math.max(320, width)} / ${isHome ? 520 : 330}`;
  }

  const target = viewport.width > 640 ? viewport : { width: 1440, height: 900 };
  const height = isHome
    ? target.width <= 900 ? 590 : Math.min(760, Math.max(610, target.height * .72))
    : 390;
  return `${target.width} / ${Math.round(height)}`;
}

function validateSection(section: SiteSectionKey, draft: SiteContent): string[] {
  const missing: string[] = [];
  const requireText = (value: string, label: string) => { if (!value.trim()) missing.push(label); };

  if (section === "branding" && draft.branding.logo.trim()) requireText(draft.branding.logoAlt, "Alternativtext des Logos");

  if (section === "hero") {
    requireText(draft.hero.title, "Titel");
    requireText(draft.hero.lead, "Einleitung");
  }
  if (section === "home") {
    requireText(draft.home.aboutTitle, "Vereins-Titel");
    requireText(draft.home.aboutText, "Vereins-Text");
    if (draft.home.noticeVisible) {
      requireText(draft.home.noticeTitle, "Willkommens-Titel");
      requireText(draft.home.noticeText, "Willkommens-Text");
      if (draft.home.noticeButtonLabel && !draft.home.noticeButtonHref) missing.push("Ziel des Willkommens-Buttons");
      if (draft.home.noticeButtonHref && !draft.home.noticeButtonLabel) missing.push("Beschriftung des Willkommens-Buttons");
    }
    if (draft.home.highlightVisible) {
      requireText(draft.home.highlightTitle, "Hinweis-Titel");
      requireText(draft.home.highlightText, "Hinweis-Text");
      if (draft.home.highlightButtonVisible) {
        requireText(draft.home.highlightButtonLabel, "Beschriftung des Hinweis-Buttons");
        requireText(draft.home.highlightButtonHref, "Ziel des Hinweis-Buttons");
      }
    }
  }
  if (section === "training") {
    requireText(draft.training.title, "Titel");
    requireText(draft.training.intro, "Einleitung");
    requireText(draft.training.location, "Trainingsort");
    requireText(draft.training.address, "Adresse");
    draft.training.slots.forEach((slot, index) => {
      requireText(slot.day, `Trainingstag ${index + 1}`);
      if (!slot.youth.trim() && !slot.adults.trim()) missing.push(`Trainingszeit für ${slot.day || `Tag ${index + 1}`}`);
    });
    if (draft.training.noteVisible) {
      requireText(draft.training.noteTitle, "Hinweis-Titel");
      requireText(draft.training.noteText, "Hinweis-Text");
      if (draft.training.noteButtonLabel && !draft.training.noteButtonHref) missing.push("Ziel des Hinweis-Buttons");
      if (draft.training.noteButtonHref && !draft.training.noteButtonLabel) missing.push("Beschriftung des Hinweis-Buttons");
    }
  }
  if (section === "chronicle") {
    requireText(draft.chronicle.title, "Titel");
    requireText(draft.chronicle.body, "Chronik-Text");
  }
  if (section === "contact") {
    requireText(draft.contact.title, "Titel");
    requireText(draft.contact.email, "E-Mail-Adresse");
    requireText(draft.contact.location, "Ort");
    requireText(draft.contact.address, "Adresse");
  }
  if (section === "board") {
    requireText(draft.board.title, "Titel");
    draft.board.members.forEach((member, index) => {
      requireText(member.name, `Name bei Vorstandsmitglied ${index + 1}`);
      requireText(member.role, `Rolle bei Vorstandsmitglied ${index + 1}`);
    });
  }
  if (section === "imprint" || section === "privacy") {
    const legal = section === "imprint" ? draft.imprint : draft.privacy;
    requireText(legal.title, "Titel");
    requireText(legal.body, "Inhalt");
  }
  if (section === "downloads") {
    requireText(draft.downloads.title, "Seitentitel");
    requireText(draft.downloads.intro, "Einleitung");
    draft.downloads.documents.forEach((document, index) => {
      requireText(document.title, `Titel bei Dokument ${index + 1}`);
      requireText(document.href, `Datei oder Link bei Dokument ${index + 1}`);
    });
  }
  return missing;
}

export function AdminContentPage() {
  const { content, refresh, loading: contentLoading } = useContent();
  const { user } = useAuth();
  const { showToast } = useAdminToast();
  const sectionStorageKey = `ttc-admin-content-section:${user?.id || "unknown"}`;
  const [draft, setDraft] = useState<SiteContent>(() => structuredClone(content));
  const [selected, setSelected] = useState<SiteSectionKey>(() => {
    try {
      const stored = window.sessionStorage.getItem(sectionStorageKey) as SiteSectionKey | null;
      return stored && sections.some((section) => section.key === stored) ? stored : "hero";
    } catch {
      return "hero";
    }
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(null);
  const [pendingDocumentDelete, setPendingDocumentDelete] = useState<DownloadDocument | null>(null);
  const [pendingBoardMemberDelete, setPendingBoardMemberDelete] = useState<{ member: BoardMember; index: number } | null>(null);
  const [boardMemberDraft, setBoardMemberDraft] = useState<BoardMember | null>(null);
  const [saved, setSaved] = useState(false);
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>(() => ({ width: window.innerWidth, height: window.innerHeight }));

  useLayoutEffect(() => {
    setDraft(structuredClone(content));
  }, [content]);

  useEffect(() => {
    try { window.sessionStorage.setItem(sectionStorageKey, selected); }
    catch { /* The selected section simply falls back to the default without browser storage. */ }
  }, [sectionStorageKey, selected]);

  useEffect(() => {
    const updateViewport = () => setPreviewViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  if (contentLoading) return <AdminPageSkeleton variant="form" />;

  const update = (field: string, value: unknown) => {
    setDraft((current) => ({ ...current, [selected]: { ...(current[selected] as unknown as Record<string, unknown>), [field]: value } } as SiteContent));
    setSaved(false);
  };
  const save = async () => {
    const missing = validateSection(selected, draft);
    if (missing.length > 0) {
      showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) });
      return;
    }
    setSaving(true);
    const { error: saveError } = await supabase.from("site_sections").upsert({ key: selected, data: draft[selected], updated_by: user?.id, updated_at: new Date().toISOString() });
    if (saveError) showToast({ type: "error", title: "Speichern fehlgeschlagen", message: adminErrorMessage(saveError, "Der Inhaltsbereich konnte nicht gespeichert werden.") });
    else {
      setSaved(true);
      await refresh();
      const sectionLabel = sections.find((section) => section.key === selected)?.label || "Inhaltsbereich";
      await writeAuditLog({ action: "content.updated", entityType: "content", entityId: selected, entityTitle: sectionLabel });
      showToast({ type: "success", title: "Änderungen gespeichert", message: `${sectionLabel} wurde auf der Website aktualisiert.` });
    }
    setSaving(false);
  };

  const uploadNoticeImage = async (section: "homeWelcome" | "homeHighlight" | "training", event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showToast({ type: "warning", title: "Falscher Dateityp", message: "Bitte wähle eine Bilddatei aus." }); input.value = ""; return; }
    if (file.size > 8 * 1024 * 1024) { showToast({ type: "warning", title: "Bild zu groß", message: "Das Bild darf höchstens 8 MB groß sein." }); input.value = ""; return; }

    setUploadingImage(true);
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `site/${section}/${crypto.randomUUID()}.${extension}`;
    const { error: uploadError } = await supabase.storage.from("media").upload(path, file, { cacheControl: "31536000", upsert: false });
    if (uploadError) showToast({ type: "error", title: "Upload fehlgeschlagen", message: adminErrorMessage(uploadError, "Das Bild konnte nicht hochgeladen werden.") });
    else {
      const { data } = supabase.storage.from("media").getPublicUrl(path);
      setDraft((current) => {
        if (section === "homeWelcome") return { ...current, home: { ...current.home, noticeImage: data.publicUrl } };
        if (section === "homeHighlight") return { ...current, home: { ...current.home, highlightImage: data.publicUrl } };
        return { ...current, training: { ...current.training, noteImage: data.publicUrl } };
      });
      setSaved(false);
      showToast({ type: "success", title: "Bild hochgeladen", message: "Das Bild ist ausgewählt. Speichere den Inhaltsbereich, um es auf der Website zu übernehmen." });
    }
    setUploadingImage(false);
    input.value = "";
  };

  const uploadBrandLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    const allowedLogoTypes = new Set(["image/png", "image/jpeg", "image/webp"]);
    if (!allowedLogoTypes.has(file.type)) {
      showToast({ type: "warning", title: "Falscher Dateityp", message: "Bitte wähle eine PNG-, JPG- oder WebP-Bilddatei als Logo aus." });
      input.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast({ type: "warning", title: "Logo zu groß", message: "Die Logo-Datei darf höchstens 5 MB groß sein." });
      input.value = "";
      return;
    }

    setUploadingImage(true);
    const extension = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `site/branding/${crypto.randomUUID()}.${extension}`;
    const { error: uploadError } = await supabase.storage.from("media").upload(path, file, { cacheControl: "31536000", upsert: false });
    if (uploadError) {
      showToast({ type: "error", title: "Logo-Upload fehlgeschlagen", message: adminErrorMessage(uploadError, "Das Logo konnte nicht hochgeladen werden.") });
    } else {
      const { data } = supabase.storage.from("media").getPublicUrl(path);
      setDraft((current) => ({ ...current, branding: { ...current.branding, logo: data.publicUrl } }));
      setSaved(false);
      showToast({ type: "success", title: "Logo hochgeladen", message: "Das neue Logo ist ausgewählt. Speichere den Bereich, um es überall zu übernehmen." });
    }
    setUploadingImage(false);
    input.value = "";
  };

  const uploadDownloadDocument = async (documentId: string, event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (file.type !== "application/pdf" && extension !== "pdf") {
      showToast({ type: "warning", title: "Falscher Dateityp", message: "Auf der Downloadseite können derzeit ausschließlich PDF-Dateien hochgeladen werden." });
      input.value = "";
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showToast({ type: "warning", title: "Dokument zu groß", message: "Eine PDF-Datei darf höchstens 20 MB groß sein." });
      input.value = "";
      return;
    }

    setUploadingDocumentId(documentId);
    const safeName = file.name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "-")
      .replace(/-{2,}/g, "-");
    const path = `site/downloads/${crypto.randomUUID()}-${safeName}`;
    const { error: uploadError } = await supabase.storage.from("media").upload(path, file, { cacheControl: "3600", contentType: "application/pdf", upsert: false });
    if (uploadError) {
      showToast({ type: "error", title: "Upload fehlgeschlagen", message: adminErrorMessage(uploadError, "Das Dokument konnte nicht hochgeladen werden.") });
    } else {
      const { data } = supabase.storage.from("media").getPublicUrl(path, { download: file.name });
      setDraft((current) => ({
        ...current,
        downloads: {
          ...current.downloads,
          documents: current.downloads.documents.map((document) => document.id === documentId ? { ...document, href: data.publicUrl } : document),
        },
      }));
      setSaved(false);
      showToast({ type: "success", title: "Dokument hochgeladen", message: `„${file.name}“ ist ausgewählt. Speichere den Inhaltsbereich, um die Datei zu veröffentlichen.` });
    }
    setUploadingDocumentId(null);
    input.value = "";
  };

  const removeDownloadDocument = () => {
    if (!pendingDocumentDelete) return;
    const title = pendingDocumentDelete.title || "Unbenanntes Dokument";
    setDraft((current) => ({
      ...current,
      downloads: {
        ...current.downloads,
        documents: current.downloads.documents.filter((document) => document.id !== pendingDocumentDelete.id),
      },
    }));
    setPendingDocumentDelete(null);
    setSaved(false);
    showToast({ type: "info", title: "Dokument entfernt", message: `„${title}“ verschwindet nach dem Speichern von der Downloadseite.` });
  };

  const removeBoardMember = () => {
    if (!pendingBoardMemberDelete) return;
    const { member, index } = pendingBoardMemberDelete;
    setDraft((current) => ({
      ...current,
      board: {
        ...current.board,
        members: current.board.members.filter((_, memberIndex) => memberIndex !== index),
      },
    }));
    setPendingBoardMemberDelete(null);
    setSaved(false);
    showToast({ type: "info", title: "Vorstandsrolle entfernt", message: `„${member.role || member.name || "Unbenannte Rolle"}“ verschwindet nach dem Speichern von der Vereinsseite.` });
  };

  const confirmBoardMemberCreate = () => {
    if (!boardMemberDraft) return;
    const member = { ...boardMemberDraft, name: boardMemberDraft.name.trim(), role: boardMemberDraft.role.trim(), email: boardMemberDraft.email.trim() };
    const missing = [!member.name && "Name", !member.role && "Funktion"].filter((field): field is string => Boolean(field));
    if (missing.length > 0) {
      showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) });
      return;
    }
    setDraft((current) => ({ ...current, board: { ...current.board, members: [...current.board.members, member] } }));
    setBoardMemberDraft(null);
    setSaved(false);
    showToast({ type: "success", title: "Vorstandsmitglied hinzugefügt", message: `${member.name} wurde als „${member.role}“ angelegt. Speichere den Inhaltsbereich, um die Änderung zu veröffentlichen.` });
  };

  return (
    <>
      <div className="admin-page-heading"><div><span>Website-Inhalte</span><h1>Seiten bearbeiten</h1><p>Texte und Informationen ändern – ohne im Quellcode zu arbeiten.</p></div><a className="admin-secondary-button" href="/" target="_blank"><Eye /> Website ansehen</a></div>
      <div className="content-admin-layout">
        <aside className="content-nav admin-panel"><span>Inhaltsbereiche</span>{sections.map((section) => <button key={section.key} className={selected === section.key ? "active" : ""} onClick={() => { setSelected(section.key); setSaved(false); }}><section.icon />{section.label}</button>)}</aside>
        <section className="admin-panel content-editor"><div className="content-editor-heading"><div><small>Bereich bearbeiten</small><h2>{sections.find((section) => section.key === selected)?.label}</h2></div><span className="save-state">{saved ? <><Check /> Gespeichert</> : "Änderungen werden erst nach dem Speichern sichtbar"}</span></div><fieldset className="content-fieldset"><div className="content-fields">{renderSection(selected, draft, update, setDraft, uploadNoticeImage, uploadBrandLogo, uploadingImage, uploadDownloadDocument, uploadingDocumentId, setPendingDocumentDelete, setPendingBoardMemberDelete, () => setBoardMemberDraft({ name: "", role: "", email: "" }), previewViewport)}</div></fieldset><div className="content-savebar"><span>{saved ? "Die Website wurde aktualisiert." : "Bitte speichere deine Änderungen."}</span><button className="admin-primary-button" onClick={() => void save()} disabled={saving || uploadingImage || Boolean(uploadingDocumentId)}><Save /> {saving ? "Speichert…" : "Änderungen speichern"}</button></div></section>
      </div>
      {pendingDocumentDelete && <AdminConfirmDialog title="Dokument entfernen?" description="Das Dokument wird aus der Liste der öffentlichen Downloads entfernt, sobald du den Inhaltsbereich speicherst." subject={`„${pendingDocumentDelete.title || "Unbenanntes Dokument"}“`} confirmLabel="Dokument entfernen" eyebrow="Downloadseite bearbeiten" note="Die hochgeladene Datei bleibt im Speicher erhalten und kann über ihren bisherigen Link erneut eingebunden werden." onCancel={() => setPendingDocumentDelete(null)} onConfirm={removeDownloadDocument} />}
      {pendingBoardMemberDelete && <AdminConfirmDialog title="Vorstandsrolle entfernen?" description="Die Person und ihre Rolle werden von der öffentlichen Vereinsseite entfernt, sobald du den Inhaltsbereich speicherst." subject={`${pendingBoardMemberDelete.member.role || "Unbenannte Rolle"} · ${pendingBoardMemberDelete.member.name || "Noch kein Name"}`} confirmLabel="Rolle entfernen" eyebrow="Vorstand bearbeiten" note="Die Änderung wird erst nach dem Speichern auf der Website sichtbar." onCancel={() => setPendingBoardMemberDelete(null)} onConfirm={removeBoardMember} />}
      {boardMemberDraft && <AdminFormDialog eyebrow="Vorstand bearbeiten" title="Vorstandsmitglied hinzufügen" description="Erfasse Person, Funktion und optional eine öffentliche Kontaktadresse." submitLabel="Mitglied hinzufügen" icon={<Users />} onCancel={() => setBoardMemberDraft(null)} onSubmit={confirmBoardMemberCreate}>
        <div className="field-grid">
          <Field label="Name" value={boardMemberDraft.name} onChange={(name) => setBoardMemberDraft((current) => current ? { ...current, name } : current)} />
          <Field label="Funktion" value={boardMemberDraft.role} onChange={(role) => setBoardMemberDraft((current) => current ? { ...current, role } : current)} />
        </div>
        <Field label="E-Mail" value={boardMemberDraft.email} hint="Optional; wird öffentlich auf der Vereinsseite angezeigt" onChange={(email) => setBoardMemberDraft((current) => current ? { ...current, email } : current)} />
        <p className="admin-form-dialog-note">Der Eintrag wird zunächst nur im Editor ergänzt. Öffentlich sichtbar wird er nach dem Speichern des Bereichs „Vorstand“.</p>
      </AdminFormDialog>}
    </>
  );
}

function Field({ label, value, onChange, multiline = false, rows = 5, hint }: { label: string; value: string; onChange: (value: string) => void; multiline?: boolean; rows?: number; hint?: string }) {
  return <label className="content-field"><span>{label}</span>{hint && <small>{hint}</small>}{multiline ? <textarea rows={rows} value={value} onChange={(event) => onChange(event.target.value)} /> : <input value={value} onChange={(event) => onChange(event.target.value)} />}</label>;
}

function HeaderImageEditor({ pageKey, label, settings, viewport, onChange }: { pageKey: HeaderPageKey; label: string; settings: HeaderImageSettings; viewport: PreviewViewport; onChange: (field: keyof HeaderImageSettings, value: string | number) => void }) {
  const isHome = pageKey === "home";
  return (
    <article className="header-crop-card">
      <div className="header-crop-heading"><span><Crop /></span><div><h3>{label}</h3><p>Ziehe das Bild in beiden Vorschauen direkt an die gewünschte Position.</p></div></div>
      <Field label="Titelbild" value={settings.image} hint="Pfad aus dem Bilderordner oder vollständige Bild-URL" onChange={(value) => onChange("image", value)} />
      <div className="header-crop-layout">
        <HeaderCropPreview label="Desktop" aspectRatio={previewAspectRatio(isHome, "desktop", viewport)} className={`header-crop-preview--desktop header-crop-preview--${isHome ? "home" : "subpage"}-desktop`} image={settings.image} x={settings.desktopX} y={settings.desktopY} onXChange={(value) => onChange("desktopX", value)} onYChange={(value) => onChange("desktopY", value)} />
        <HeaderCropPreview label="Handy" aspectRatio={previewAspectRatio(isHome, "mobile", viewport)} className={`header-crop-preview--mobile header-crop-preview--${isHome ? "home" : "subpage"}-mobile`} image={settings.image} x={settings.mobileX} y={settings.mobileY} onXChange={(value) => onChange("mobileX", value)} onYChange={(value) => onChange("mobileY", value)} />
      </div>
    </article>
  );
}

function HeaderCropPreview({ label, className, aspectRatio, image, x, y, onXChange, onYChange }: { label: string; className: string; aspectRatio: string; image: string; x: number; y: number; onXChange: (value: number) => void; onYChange: (value: number) => void }) {
  const previewRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; clientX: number; clientY: number; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const clamp = (value: number) => Math.max(0, Math.min(100, value));

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!image) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, x, y };
    setDragging(true);
  };

  const moveImage = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    const preview = previewRef.current;
    const previewImage = preview?.querySelector("img");
    if (!start || !preview || !previewImage || start.pointerId !== event.pointerId) return;

    const bounds = preview.getBoundingClientRect();
    const scale = Math.max(bounds.width / previewImage.naturalWidth, bounds.height / previewImage.naturalHeight);
    const overflowX = Math.max(0, previewImage.naturalWidth * scale - bounds.width);
    const overflowY = Math.max(0, previewImage.naturalHeight * scale - bounds.height);
    if (overflowX > 0.5) onXChange(clamp(start.x - ((event.clientX - start.clientX) / overflowX) * 100));
    if (overflowY > 0.5) onYChange(clamp(start.y - ((event.clientY - start.clientY) / overflowY) * 100));
  };

  const stopDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <div className="header-crop-variant">
      <div className="header-crop-label"><span>{label}</span>{image && <button type="button" onClick={() => { onXChange(50); onYChange(50); }}>Zentrieren</button>}</div>
      <div
        ref={previewRef}
        className={`header-crop-preview ${className} ${dragging ? "header-crop-preview--dragging" : ""}`}
        style={{ aspectRatio }}
        role="img"
        aria-label={`${label}: Bildausschnitt durch Ziehen positionieren`}
        tabIndex={image ? 0 : -1}
        onPointerDown={startDrag}
        onPointerMove={moveImage}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 5 : 1;
          if (event.key === "ArrowLeft") { event.preventDefault(); onXChange(clamp(x - step)); }
          if (event.key === "ArrowRight") { event.preventDefault(); onXChange(clamp(x + step)); }
          if (event.key === "ArrowUp") { event.preventDefault(); onYChange(clamp(y - step)); }
          if (event.key === "ArrowDown") { event.preventDefault(); onYChange(clamp(y + step)); }
        }}
      >
        {image ? <img src={image} alt="" draggable={false} style={{ objectPosition: `${x}% ${y}%` }} /> : <span className="header-crop-empty">Kein Bild ausgewählt</span>}
        {image && <><i aria-hidden="true" /><span className="header-crop-drag-hint">{dragging ? "Bildausschnitt verschieben" : "Bild mit Maus oder Finger ziehen"}</span></>}
      </div>
    </div>
  );
}

interface NoticeEditorProps {
  pageLabel: string;
  showImageControls?: boolean;
  visible: boolean;
  title: string;
  text: string;
  image: string;
  buttonVisible?: boolean;
  buttonLabel: string;
  buttonHref: string;
  uploadingImage: boolean;
  onVisibleChange: (value: boolean) => void;
  onTitleChange: (value: string) => void;
  onTextChange: (value: string) => void;
  onImageChange: (value: string) => void;
  onButtonVisibleChange?: (value: boolean) => void;
  onButtonLabelChange: (value: string) => void;
  onButtonHrefChange: (value: string) => void;
  onImageUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
}

function NoticeEditor({ pageLabel, showImageControls = true, visible, title, text, image, buttonVisible, buttonLabel, buttonHref, uploadingImage, onVisibleChange, onTitleChange, onTextChange, onImageChange, onButtonVisibleChange, onButtonLabelChange, onButtonHrefChange, onImageUpload }: NoticeEditorProps) {
  const hasButtonToggle = typeof buttonVisible === "boolean" && Boolean(onButtonVisibleChange);
  const showButtonFields = hasButtonToggle ? buttonVisible : true;

  return (
    <div className="training-notice-admin content-notice-admin">
      <label className="content-toggle">
        <input type="checkbox" checked={visible} onChange={(event) => onVisibleChange(event.target.checked)} />
        <span className="content-toggle-track"><span /></span>
        <span><strong>Hinweis anzeigen</strong><small>Blendet den vollständigen Hinweis{showImageControls ? " inklusive optionalem Bild" : ""} auf der {pageLabel} ein oder aus.</small></span>
      </label>
      <div className={`training-notice-settings ${visible ? "" : "training-notice-settings--muted"}`}>
        <Field label="Hinweis-Titel" value={title} onChange={onTitleChange} />
        <Field label="Hinweis-Text" value={text} multiline onChange={onTextChange} />
        {hasButtonToggle && <label className="content-toggle content-toggle--button">
          <input type="checkbox" checked={buttonVisible} onChange={(event) => onButtonVisibleChange?.(event.target.checked)} />
          <span className="content-toggle-track"><span /></span>
          <span><strong>Button anzeigen</strong><small>Der Hinweis kann auch ohne Aktionsbutton veröffentlicht werden.</small></span>
        </label>}
        {showButtonFields && <div className="field-grid">
          <Field label="Button-Beschriftung" value={buttonLabel} hint={hasButtonToggle ? "Text auf dem Button" : "Leer lassen, um den Button auszublenden"} onChange={onButtonLabelChange} />
          <Field label="Button-Ziel" value={buttonHref} hint="Interner Pfad, vollständige URL oder mailto:-Adresse" onChange={onButtonHrefChange} />
        </div>}
        {showImageControls && <div className="content-image-control">
          <div className="content-image-heading"><span><ImageIcon /></span><div><strong>Optionales Hinweisbild</strong><small>Das Bildfeld darf leer bleiben. Empfohlenes Format: Querformat.</small></div></div>
          {image && <img src={image} alt="Vorschau des Hinweisbilds" />}
          <label className="content-image-upload"><UploadCloud /> {uploadingImage ? "Bild wird hochgeladen…" : "Neues Bild hochladen"}<input type="file" accept="image/*" disabled={uploadingImage} onChange={(event) => void onImageUpload(event)} /></label>
          <Field label="Bildpfad oder Bild-URL" value={image} hint="Leer lassen, um den Hinweis ohne Bild darzustellen" onChange={onImageChange} />
        </div>}
      </div>
    </div>
  );
}

function renderSection(
  selected: SiteSectionKey,
  draft: SiteContent,
  update: (field: string, value: unknown) => void,
  setDraft: Dispatch<SetStateAction<SiteContent>>,
  uploadNoticeImage: (section: "homeWelcome" | "homeHighlight" | "training", event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  uploadBrandLogo: (event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  uploadingImage: boolean,
  uploadDownloadDocument: (documentId: string, event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  uploadingDocumentId: string | null,
  requestDocumentDelete: Dispatch<SetStateAction<DownloadDocument | null>>,
  requestBoardMemberDelete: Dispatch<SetStateAction<{ member: BoardMember; index: number } | null>>,
  requestBoardMemberCreate: () => void,
  previewViewport: PreviewViewport,
): ReactNode {
  if (selected === "branding") {
    return (
      <div className="branding-admin-editor">
        <div className="branding-preview-card">
          <span>Aktuelles Logo</span>
          <div className={draft.branding.logo ? "branding-logo-preview branding-logo-preview--image" : "branding-logo-preview"}>
            {draft.branding.logo ? <img src={draft.branding.logo} alt={draft.branding.logoAlt || "Vereinslogo"} /> : <span className="brand-mark"><span className="brand-paddle" /><span className="brand-ball" /></span>}
          </div>
          <p>{draft.branding.logo ? "Dieses Logo wird in Navigation, Footer, Login und Adminbereich verwendet." : "Aktuell wird das bestehende TTC-Signet als Fallback verwendet."}</p>
        </div>
        <div className="branding-admin-controls">
          <div><h3>Vereinslogo austauschen</h3><p>Am besten funktioniert ein quadratisches oder rundes Logo mit transparentem Hintergrund.</p></div>
          <label className={`branding-logo-upload ${uploadingImage ? "is-uploading" : ""}`}><UploadCloud /><span><strong>{uploadingImage ? "Logo wird hochgeladen…" : "Neues Logo hochladen"}</strong><small>PNG, JPG oder WebP · maximal 5 MB</small></span><input type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" disabled={uploadingImage} onChange={(event) => void uploadBrandLogo(event)} /></label>
          <Field label="Logo-Pfad oder Bild-URL" value={draft.branding.logo} hint="Leer lassen, um wieder das bisherige TTC-Signet zu verwenden" onChange={(value) => update("logo", value)} />
          <Field label="Alternativtext" value={draft.branding.logoAlt} hint="Beschreibt das Logo für Screenreader" onChange={(value) => update("logoAlt", value)} />
          {draft.branding.logo && <button type="button" className="branding-logo-reset" onClick={() => update("logo", "")}><Trash2 /> Eigenes Logo entfernen und Fallback verwenden</button>}
        </div>
      </div>
    );
  }

  if (selected === "hero") {
    return (
      <>
        <div className="field-grid">
          <Field label="Kleine Überschrift" value={draft.hero.eyebrow} onChange={(value) => update("eyebrow", value)} />
          <Field label="Titel" value={draft.hero.title} onChange={(value) => update("title", value)} />
        </div>
        <Field label="Einleitung" value={draft.hero.lead} multiline onChange={(value) => update("lead", value)} />
        <div className="field-grid">
          <Field label="Erster Button" value={draft.hero.primaryLabel} onChange={(value) => update("primaryLabel", value)} />
          <Field label="Zweiter Button" value={draft.hero.secondaryLabel} onChange={(value) => update("secondaryLabel", value)} />
        </div>
      </>
    );
  }

  if (selected === "headers") {
    return (
      <div className="header-crop-list">
        {headerPages.map((page) => (
          <HeaderImageEditor
            key={page.key}
            pageKey={page.key}
            label={page.label}
            settings={draft.headers[page.key]}
            viewport={previewViewport}
            onChange={(field, value) => update(page.key, { ...draft.headers[page.key], [field]: value })}
          />
        ))}
      </div>
    );
  }

  if (selected === "home") {
    return (
      <>
        <h3 className="field-group-title">Willkommenskasten „Lust auf Tischtennis?“</h3>
        <NoticeEditor
          pageLabel="Startseite direkt unter dem Titelbild"
          showImageControls={false}
          visible={draft.home.noticeVisible}
          title={draft.home.noticeTitle}
          text={draft.home.noticeText}
          image={draft.home.noticeImage}
          buttonLabel={draft.home.noticeButtonLabel}
          buttonHref={draft.home.noticeButtonHref}
          onVisibleChange={(value) => update("noticeVisible", value)}
          onTitleChange={(value) => update("noticeTitle", value)}
          onTextChange={(value) => update("noticeText", value)}
          onImageChange={(value) => update("noticeImage", value)}
          onButtonLabelChange={(value) => update("noticeButtonLabel", value)}
          onButtonHrefChange={(value) => update("noticeButtonHref", value)}
          onImageUpload={(event) => uploadNoticeImage("homeWelcome", event)}
          uploadingImage={uploadingImage}
        />
        <h3 className="field-group-title">Zusätzlicher Hinweis unter dem Willkommenskasten</h3>
        <NoticeEditor
          pageLabel="Startseite unter dem Willkommenskasten"
          visible={draft.home.highlightVisible}
          title={draft.home.highlightTitle}
          text={draft.home.highlightText}
          image={draft.home.highlightImage}
          buttonVisible={draft.home.highlightButtonVisible}
          buttonLabel={draft.home.highlightButtonLabel}
          buttonHref={draft.home.highlightButtonHref}
          onVisibleChange={(value) => update("highlightVisible", value)}
          onTitleChange={(value) => update("highlightTitle", value)}
          onTextChange={(value) => update("highlightText", value)}
          onImageChange={(value) => update("highlightImage", value)}
          onButtonVisibleChange={(value) => update("highlightButtonVisible", value)}
          onButtonLabelChange={(value) => update("highlightButtonLabel", value)}
          onButtonHrefChange={(value) => update("highlightButtonHref", value)}
          onImageUpload={(event) => uploadNoticeImage("homeHighlight", event)}
          uploadingImage={uploadingImage}
        />
        <Field label="Vereins-Titel" value={draft.home.aboutTitle} onChange={(value) => update("aboutTitle", value)} />
        <Field label="Vereins-Text" value={draft.home.aboutText} multiline onChange={(value) => update("aboutText", value)} />
        <h3 className="field-group-title">Vereinswerte</h3>
        {draft.home.values.map((item, index) => (
          <div className="repeater-row" key={index}>
            <Field label={`Wert ${index + 1}`} value={item.title} onChange={(value) => setDraft((current) => ({ ...current, home: { ...current.home, values: current.home.values.map((entry, entryIndex) => entryIndex === index ? { ...entry, title: value } : entry) } }))} />
            <Field label="Beschreibung" value={item.text} onChange={(value) => setDraft((current) => ({ ...current, home: { ...current.home, values: current.home.values.map((entry, entryIndex) => entryIndex === index ? { ...entry, text: value } : entry) } }))} />
          </div>
        ))}
      </>
    );
  }

  if (selected === "training") {
    return (
      <>
        <Field label="Titel" value={draft.training.title} onChange={(value) => update("title", value)} />
        <Field label="Einleitung" value={draft.training.intro} multiline onChange={(value) => update("intro", value)} />
        <div className="field-grid">
          <Field label="Ort" value={draft.training.location} onChange={(value) => update("location", value)} />
          <Field label="Adresse" value={draft.training.address} onChange={(value) => update("address", value)} />
        </div>
        <h3 className="field-group-title">Trainingszeiten</h3>
        {draft.training.slots.map((slot, index) => (
          <div className="repeater-row repeater-row--three" key={index}>
            <Field label="Wochentag" value={slot.day} onChange={(value) => setDraft((current) => ({ ...current, training: { ...current.training, slots: current.training.slots.map((entry, entryIndex) => entryIndex === index ? { ...entry, day: value } : entry) } }))} />
            <Field label="Jugend" value={slot.youth} onChange={(value) => setDraft((current) => ({ ...current, training: { ...current.training, slots: current.training.slots.map((entry, entryIndex) => entryIndex === index ? { ...entry, youth: value } : entry) } }))} />
            <Field label="Erwachsene" value={slot.adults} onChange={(value) => setDraft((current) => ({ ...current, training: { ...current.training, slots: current.training.slots.map((entry, entryIndex) => entryIndex === index ? { ...entry, adults: value } : entry) } }))} />
          </div>
        ))}
        <h3 className="field-group-title">Hinweisbereich</h3>
        <NoticeEditor
          pageLabel="Trainingsseite"
          visible={draft.training.noteVisible}
          title={draft.training.noteTitle}
          text={draft.training.noteText}
          image={draft.training.noteImage}
          buttonLabel={draft.training.noteButtonLabel}
          buttonHref={draft.training.noteButtonHref}
          onVisibleChange={(value) => update("noteVisible", value)}
          onTitleChange={(value) => update("noteTitle", value)}
          onTextChange={(value) => update("noteText", value)}
          onImageChange={(value) => update("noteImage", value)}
          onButtonLabelChange={(value) => update("noteButtonLabel", value)}
          onButtonHrefChange={(value) => update("noteButtonHref", value)}
          onImageUpload={(event) => uploadNoticeImage("training", event)}
          uploadingImage={uploadingImage}
        />
      </>
    );
  }

  if (selected === "chronicle") {
    return <><Field label="Titel" value={draft.chronicle.title} onChange={(value) => update("title", value)} /><Field label="Einleitung" value={draft.chronicle.intro} multiline onChange={(value) => update("intro", value)} /><Field label="Chronik-Text" value={draft.chronicle.body} multiline hint="Leerzeilen trennen die Absätze" onChange={(value) => update("body", value)} /></>;
  }

  if (selected === "contact") {
    return <><Field label="Titel" value={draft.contact.title} onChange={(value) => update("title", value)} /><Field label="Einleitung" value={draft.contact.intro} multiline onChange={(value) => update("intro", value)} /><div className="field-grid"><Field label="E-Mail" value={draft.contact.email} onChange={(value) => update("email", value)} /><Field label="Telefon" value={draft.contact.phone} onChange={(value) => update("phone", value)} /></div><div className="field-grid"><Field label="Ort" value={draft.contact.location} onChange={(value) => update("location", value)} /><Field label="Adresse" value={draft.contact.address} onChange={(value) => update("address", value)} /></div></>;
  }

  if (selected === "downloads") {
    const documents = draft.downloads.documents;
    const updateDocument = (documentId: string, field: "title" | "text" | "href", value: string) => {
      update("documents", documents.map((document) => document.id === documentId ? { ...document, [field]: value } : document));
    };
    const moveDocument = (index: number, direction: -1 | 1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= documents.length) return;
      const nextDocuments = [...documents];
      [nextDocuments[index], nextDocuments[targetIndex]] = [nextDocuments[targetIndex], nextDocuments[index]];
      update("documents", nextDocuments);
    };
    const addDocument = () => {
      update("documents", [...documents, { id: crypto.randomUUID(), title: "Neues Dokument", text: "", href: "" }]);
    };

    return (
      <>
        <div className="field-grid">
          <Field label="Kleine Überschrift" value={draft.downloads.eyebrow} onChange={(value) => update("eyebrow", value)} />
          <Field label="Seitentitel" value={draft.downloads.title} onChange={(value) => update("title", value)} />
        </div>
        <Field label="Einleitung" value={draft.downloads.intro} multiline rows={3} onChange={(value) => update("intro", value)} />
        <div className="download-editor-heading">
          <div><h3>Dokumente</h3><p>Reihenfolge, Texte und verknüpfte PDF-Dateien verwalten.</p></div>
          <button type="button" className="admin-secondary-button" onClick={addDocument}><Plus /> Dokument hinzufügen</button>
        </div>
        <div className="download-editor-list">
          {documents.map((document, index) => (
            <article className="download-editor-card" key={document.id}>
              <div className="download-editor-card-head">
                <span className="download-editor-icon"><FileText /></span>
                <div><small>Dokument {index + 1}</small><strong>{document.title || "Unbenanntes Dokument"}</strong></div>
                <div className="download-editor-actions">
                  <button type="button" onClick={() => moveDocument(index, -1)} disabled={index === 0} aria-label={`${document.title || "Dokument"} nach oben verschieben`}><ChevronUp /></button>
                  <button type="button" onClick={() => moveDocument(index, 1)} disabled={index === documents.length - 1} aria-label={`${document.title || "Dokument"} nach unten verschieben`}><ChevronDown /></button>
                  <button type="button" className="danger" onClick={() => requestDocumentDelete(document)} aria-label={`${document.title || "Dokument"} löschen`}><Trash2 /></button>
                </div>
              </div>
              <div className="field-grid">
                <Field label="Titel" value={document.title} onChange={(value) => updateDocument(document.id, "title", value)} />
                <Field label="Beschreibung" value={document.text} hint="Optionaler kurzer Text unter dem Titel" onChange={(value) => updateDocument(document.id, "text", value)} />
              </div>
              <Field label="Dateipfad oder Download-URL" value={document.href} hint="Kann auch auf eine vorhandene Datei im Repository oder eine externe URL zeigen" onChange={(value) => updateDocument(document.id, "href", value)} />
              <label className={`document-upload-button ${uploadingDocumentId === document.id ? "is-uploading" : ""}`}>
                <UploadCloud />
                <span><strong>{uploadingDocumentId === document.id ? "PDF wird hochgeladen…" : "PDF hochladen oder ersetzen"}</strong><small>Maximal 20 MB; der neue Link wird automatisch eingetragen.</small></span>
                <input type="file" accept="application/pdf,.pdf" disabled={Boolean(uploadingDocumentId)} onChange={(event) => void uploadDownloadDocument(document.id, event)} />
              </label>
            </article>
          ))}
          {documents.length === 0 && <div className="download-editor-empty"><Files /><strong>Noch keine Dokumente</strong><p>Füge ein Dokument hinzu, um es auf der Downloadseite anzubieten.</p><button type="button" className="admin-secondary-button" onClick={addDocument}><Plus /> Erstes Dokument hinzufügen</button></div>}
        </div>
      </>
    );
  }

  if (selected === "board") {
    const members = draft.board.members;
    const updateMember = (index: number, field: keyof BoardMember, value: string) => {
      update("members", members.map((member, memberIndex) => memberIndex === index ? { ...member, [field]: value } : member));
    };

    return (
      <>
        <Field label="Titel" value={draft.board.title} onChange={(value) => update("title", value)} />
        <Field label="Einleitung" value={draft.board.intro} multiline onChange={(value) => update("intro", value)} />
        <div className="board-editor-heading">
          <div><h3>Vorstandsrollen</h3><p>Personen, Funktionen und Kontaktdaten der Vereinsleitung verwalten.</p></div>
          <button type="button" className="admin-secondary-button" onClick={requestBoardMemberCreate}><Plus /> Rolle hinzufügen</button>
        </div>
        <div className="board-editor-list">
          {members.map((member, index) => (
            <article className="board-member-editor" key={index}>
              <div className="board-member-editor-head">
                <span><Users /></span>
                <div><small>Vorstandsrolle {index + 1}</small><strong>{member.role || "Neue Rolle"}</strong></div>
                <button type="button" onClick={() => requestBoardMemberDelete({ member, index })} aria-label={`${member.role || "Vorstandsrolle"} entfernen`}><Trash2 /></button>
              </div>
              <div className="board-member-fields">
                <Field label="Name" value={member.name} onChange={(value) => updateMember(index, "name", value)} />
                <Field label="Funktion" value={member.role} onChange={(value) => updateMember(index, "role", value)} />
                <Field label="E-Mail" value={member.email} hint="Optional" onChange={(value) => updateMember(index, "email", value)} />
              </div>
            </article>
          ))}
          {members.length === 0 && <div className="board-editor-empty"><Users /><strong>Noch keine Vorstandsrollen</strong><p>Füge eine Rolle hinzu, um sie auf der Vereinsseite anzuzeigen.</p><button type="button" className="admin-secondary-button" onClick={requestBoardMemberCreate}><Plus /> Erste Rolle hinzufügen</button></div>}
        </div>
      </>
    );
  }

  const legal = selected === "imprint" ? draft.imprint : draft.privacy;
  return (
    <>
      <Field label="Seitentitel" value={legal.title} onChange={(value) => update("title", value)} />
      <Field label="Einleitung" value={legal.intro} multiline rows={3} onChange={(value) => update("intro", value)} />
      <Field label="Rechtstext" value={legal.body} multiline rows={18} hint="Zwischenüberschriften mit ## beginnen; Leerzeilen trennen Absätze." onChange={(value) => update("body", value)} />
    </>
  );
}
